<script setup>
import axios from 'axios'
import { ref, watch, computed } from 'vue'
import { Head, router, usePage } from '@inertiajs/vue3'
import Layout from '@pages/Shared/layouts/StaffLayout.vue'
import Modal from '@pages/staff/components/ClassModal.vue'
import Table from '@pages/Shared/ui/Table.vue'
import Prompt from '@pages/Shared/Prompt.vue'
import { PhBank } from "@phosphor-icons/vue"
import { flashToast } from "@/toast.js"
import { capitalize } from '@/helpers.js'

const props = defineProps({
    classes:  Object,
    sections: Array,
    filters:  Object,  
})

const openModal      = ref(false)
const modalStatus    = ref(null)
const modalTitle     = ref('')
const showPrompt     = ref(false)
const selectedClass  = ref(null)
const deleteId       = ref(null)

const columns = [
    { key: 'index',        label: '#',       type: 'index'   },
    { key: 'name',         label: 'Name'                     },
    { key: 'section.name', label: 'Section'                  },
    { key: 'actions',      label: '',        type: 'actions' },
]

const page = usePage()
const can  = computed(() => page.props.can || {})

const filters = ref({ search: null, section: null, ...props.filters });

const sectionOptions = computed(() =>
    (props.sections ?? []).map(s => ({ label: capitalize(s.name), value: s.id }))
)

watch(() => props.filters, (val) => { filters.value = { ...val } }, { deep: true })  

const applyFilters = () => {
    const cleaned = Object.fromEntries(
        Object.entries(filters.value).filter(([_, v]) => v !== null && v !== undefined)
    )
    router.get('/staff/manage-classes', { ...cleaned, filter: 1 }, {
        preserveState: true, preserveScroll: true, replace: true,
    })
}

const handleSearch = (value) => { filters.value.search = value; applyFilters() }  
const handleFilter = (newFilters) => { 
  filters.value = { ...filters.value, ...newFilters }; applyFilters() 
}  

const openAddClassModal = () => {
    selectedClass.value = null
    modalTitle.value    = 'Add Class'
    modalStatus.value   = 'create'
    openModal.value     = true
}

const editClass = (id) => {
    selectedClass.value = props.classes.data.find(c => c.id === id) ?? null
    modalTitle.value    = 'Edit Class'
    modalStatus.value   = 'edit'
    openModal.value     = true
}

const viewClass = (id) => {
    selectedClass.value = props.classes.data.find(c => c.id === id) ?? null
    modalTitle.value    = 'View Class'
    modalStatus.value   = 'view'
    openModal.value     = true
}

const deleteClass = (id) => { deleteId.value = id; showPrompt.value = true }

const confirmDelete = () => {
    router.delete(`/staff/manage-classes/${deleteId.value}`, {
        onSuccess: (page) => { showPrompt.value = false; flashToast(page.props.flash) }
    })
}

const handleSubmit = (form) => {
    const options = {
        onSuccess: (page) => { openModal.value = false; flashToast(page.props.flash) }
    }
    if (modalStatus.value === 'edit') {
        form._method = 'put'
        form.post(`/staff/manage-classes/${form.id}`, options)
    } else {
        form.post('/staff/manage-classes', options)
    }
}
</script>

<template>
  <Head title="Classes Management" />
  <Layout>

    <Prompt
      title="class"
      :isShow="showPrompt"
      @cancel="showPrompt = false"
      @delete="confirmDelete"
    />

    <Modal
      v-model="openModal"
      :title="modalTitle"
      :status="modalStatus"
      :classRoom="selectedClass"
      :sections="sectionOptions"
      @submitted="handleSubmit"
    />

    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h2 class="text-xl font-bold text-primary">Classes</h2>
        <p class="text-sm text-gray-500 mt-0.5">Manage all classes</p>
      </div>
      <button
        v-if="can.createClass"
        @click="openAddClassModal"
        class="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
          text-white bg-primary hover:bg-primary-light rounded-lg transition"
      >
        <PhBank :size="20" />
        Add Class
      </button>
    </div>

    <Table
      :data="classes"
      :columns="columns"
      variant="staff"
      row-key="id"
      :exportable="true"
      :actions="{ view: true, edit: true, delete: true }"
      :filters="[
        {
          key: 'section', label: 'Section',
          options: [
            { label: 'All Sections', value: null },
            ...sectionOptions,
          ],
          value: filters.section,
        },
      ]"
      @search="handleSearch"
      @filter="handleFilter"
      @edit="editClass"
      @view="viewClass"
      @delete="deleteClass"
    />

  </Layout>
</template>